import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Truck } from "lucide-react";
import { api, resolveMediaUrl } from "@/lib/api";
import { getPlatformMeta } from "@/lib/socialPlatforms";
import { useLang } from "@/i18n/LangContext";

const NAV = [
    { id: "o-projekcie", label: "O projekcie" },
    { id: "galeria", label: "Galeria" },
    { id: "mapa", label: "Interaktywna mapa" },
    { id: "dodatki", label: "Dodatki" },
    { id: "pobierz", label: "Pobierz" },
    { id: "archiwum", label: "Archiwum" },
    { id: "zespol", label: "Zespół" },
    { id: "kontakt", label: "Społeczność" },
];

export default function Footer() {
    const [links, setLinks] = useState([]);
    const [logoUrl, setLogoUrl] = useState("");
    const location = useLocation();
    const navigate = useNavigate();
    const { t, lp, lang } = useLang();
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/social");
                setLinks(data);
            } catch (e) {
                /* ignore */
            }
        })();
        (async () => {
            try {
                const { data } = await api.get("/mod-info");
                setLogoUrl(data.logo_url || "");
            } catch (e) {
                /* ignore */
            }
        })();
    }, []);

    const goSection = (id) => {
        // From a subpage, route to home with the hash (Home handles the scroll);
        // on the home page, scroll smoothly to the section directly.
        const isHome = location.pathname === "/" || location.pathname === "/en";
        if (!isHome) {
            navigate(lang === "en" ? `/en#${id}` : `/#${id}`);
            return;
        }
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <footer
            data-testid="site-footer"
            className="border-t border-white/5 mt-24 bg-[color:var(--bg-surface)]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
                <div>
                    <div className="flex items-center gap-3 mb-4">
                        {logoUrl ? (
                            <img
                                src={resolveMediaUrl(logoUrl)}
                                alt="Project Ruda Śląska"
                                loading="lazy"
                                decoding="async"
                                className="h-10 w-auto object-contain"
                                data-testid="footer-logo"
                            />
                        ) : (
                            <span className="w-9 h-9 flex items-center justify-center rounded-sm border border-[color:var(--border-strong)] text-[color:var(--primary)] font-display font-bold">
                                PR
                            </span>
                        )}
                        <span className="font-display font-semibold uppercase tracking-wider text-white">
                            Project Ruda Śląska
                        </span>
                    </div>
                    <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed max-w-sm">
                        {t("Niezależna modyfikacja mapy do Euro Truck Simulator 2 odwzorowująca miasto Ruda Śląska w skali 1:1.")}
                    </p>
                </div>
                <div>
                    <div className="label-eyebrow mb-3">{t("Nawigacja")}</div>
                    <ul className="space-y-2 text-sm text-[color:var(--text-secondary)]">
                        {NAV.map((n) => (
                            <li key={n.id}>
                                <button
                                    type="button"
                                    data-testid={`footer-nav-${n.id}`}
                                    onClick={() => goSection(n.id)}
                                    className="text-left hover:text-[color:var(--primary)] transition-colors"
                                >
                                    {t(n.label)}
                                </button>
                            </li>
                        ))}
                        <li>
                            <Link
                                to={lp("/zglos-blad")}
                                data-testid="footer-nav-report-bug"
                                className="hover:text-[color:var(--primary)] transition-colors"
                            >
                                {t("Zgłoś błąd")}
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/regulamin"
                                data-testid="footer-nav-regulations"
                                className="hover:text-[color:var(--primary)] transition-colors"
                            >
                                {t("Regulamin")}
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/polityka-prywatnosci"
                                data-testid="footer-nav-privacy"
                                className="hover:text-[color:var(--primary)] transition-colors"
                            >
                                {t("Polityka prywatności")}
                            </Link>
                        </li>
                    </ul>
                </div>
                <div>
                    <div className="label-eyebrow mb-3">{t("Społeczność")}</div>
                    {links.length > 0 ? (
                        <div className="flex flex-wrap gap-3" data-testid="footer-social">
                            {links.slice(0, 8).map((link) => {
                                const meta = getPlatformMeta(link.platform);
                                const Icon = meta.Icon;
                                return (
                                    <a
                                        key={link.id}
                                        href={link.url.startsWith("http") || link.url.startsWith("mailto:") ? link.url : `https://${link.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-10 h-10 inline-flex items-center justify-center rounded-md border border-white/10 transition-colors"
                                        style={{ color: meta.color }}
                                        aria-label={link.label || meta.label}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = `${meta.color}80`;
                                            e.currentTarget.style.background = `${meta.color}15`;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                                            e.currentTarget.style.background = "transparent";
                                        }}
                                    >
                                        <Icon size={16} />
                                    </a>
                                );
                            })}
                        </div>
                    ) : (
                        <a
                            href="#kontakt"
                            className="text-sm text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] inline-flex items-center gap-2"
                        >
                            <Truck size={14} /> {t("Dodaj kanały społecznościowe w panelu admina")}
                        </a>
                    )}
                    <p className="text-xs text-[color:var(--text-muted)] mt-6">
                        © {new Date().getFullYear()}{" "}
                        {t("Project Ruda Śląska. Modyfikacja fanowska. Euro Truck Simulator 2 jest znakiem towarowym SCS Software.")}
                    </p>
                </div>
            </div>
        </footer>
    );
}
